import { getAllServices } from 'api';

import { computedFn } from 'mobx-utils';

class ServicesStore {
  categories = [];

  services = [];

  loading = true;

  error = false;

  loadServices() {
    this.loading = true;
    getAllServices()
      .then(response => {
        // set services
        this.services = response.data.sort((a, b) => a.name.localeCompare(b.name));
        // compute categories by looking at services data
        const categories = new Set();
        response.data.forEach(service => {
          categories.add(service.category);
        });
        this.categories = [...categories];
      })
      .catch(() => {
        this.error = true;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  // Return the id for the given service name(s)
  getServiceId = computedFn(services => {
    if (typeof services === 'string') {
      return this.services.find(s => s.name === services).id;
    }
    const serviceIds = [];
    services.forEach(service => serviceIds.push(this.services.find(s => s.name === service).id));
    return serviceIds;
  });

  // Return the name for the given service caseType(s)
  getServiceName = computedFn(caseTypes => {
    if (typeof caseTypes === 'string') {
      const service = this.services.find(s => s.caseType === caseTypes);
      return service ? service.name : '';
    }
    const serviceNames = [];
    caseTypes.forEach(caseType => {
      const service = this.services.find(s => s.caseType === caseType);
      if (service) {
        serviceNames.push(service.name);
      }
    });
    return serviceNames;
  });

  // Return the caseType for the given service name
  getCaseType = computedFn(serviceName => {
    const service = this.services.find(s => s.name === serviceName);
    return service ? service.caseType : null;
  });

  // Categorize the given list of services
  categorizeServices = computedFn(servicesList => {
    const retval = {};
    this.categories.forEach(category => {
      retval[category] = servicesList?.filter(value =>
        this.servicesByCategory[category].includes(value),
      );
    });
    return retval;
  });

  get serviceOptions() {
    if (this.loading) {
      return [];
    }
    return this.services.map(service => {
      return [service.id, service.name];
    });
  }

  // Return all services grouped by category
  get servicesByCategory() {
    if (this.loading) {
      return {};
    }
    const result = {};
    // Create a entry in the dict for each category
    this.categories.forEach(category => {
      result[category] = [];
    });
    // Add services to the corresponding category entry
    this.services.forEach(service => {
      result[service.category].push(service.name);
    });
    return result;
  }

  get servicesSortedByCaseType() {
    if (this.loading) {
      return [];
    }
    return this.services.slice().sort((a, b) => a.caseType - b.caseType);
  }

  get servicesSortedByCategoryAndName() {
    if (this.loading) {
      return [];
    }
    return this.services.slice().sort((a, b) => a.category.localeCompare(b.category));
  }

  get servicesSortedByCategoryAndCaseType() {
    if (this.loading) {
      return [];
    }
    return this.services
      .slice()
      .sort((a, b) => a.caseType - b.caseType)
      .sort((a, b) => a.category.localeCompare(b.category));
  }
}

export default ServicesStore;
